import Component, {TagName, Template} from "./Component";
import {$each, $if} from "./Templating";
import Timing from "./Timing";
import AspectRatio from "./AspectRatio";
import QualitySlide, {SlideData} from "./QualitySlide";

type seconds = number;
type milliseconds = number;

interface SlideshowData {
	slides: SlideData[];
	controls?: ControlsData|boolean;
	speed?: seconds;
	autoPlay?: boolean;
	aspectRatio?: string;
	maxHeight?: string;
	minHeight?: string;
}

export {SlideshowData};

const slideshowSchema = {
	slides: "object"
};

interface ControlsData {
	arrows?: boolean;
	dots?: boolean;
}

export {ControlsData};

const controlsView = (controls: QualityControls) => `
	${ $if(controls.data.arrows, () => `
		<a href="#" class="left" data-action="left" title="Left"></a>
		<a href="#" class="right" data-action="right" title="right"></a>
	`) }

	${ $if(controls.data.dots, () => `
		<div class="dots">
			${ $each(controls.$parent.slides, (slide, i) => `
				<div class="dot" role="button" data-slide="${ i }">
				<svg version="1.1"
					 viewBox="-20 -20 340 340"
				     xmlns="http://www.w3.org/2000/svg">

				  <polygon class="hex-bullet" points="300,150 225,280 75,280 0,150 75,20 225,20" />
				</svg>
				</div>
			`) }
		</div>
	`) }
`;

@TagName("quality-slideshow-controls")
@Template(controlsView)
export class QualityControls extends Component {
	$parent: QualitySlideshow;

	data: ControlsData;

	constructor(params) {
		super(params);

		this.data = params;
	}

	static $for(parent: QualitySlideshow, label: any, params: ControlsData) {
		const inst = <QualityControls>super.$for(parent, label, params);

		parent.onSwitchSlides((index: number) => {
			const dots = inst.getDots();
			const activeDot = inst.getDot(index);

			dots.forEach(dot => {
				if (dot !== activeDot) {
					dot.removeAttribute("data-active");
				} else {
					dot.setAttribute("data-active", "true");
				}
			});
		});

		return inst;
	}

	getDots() {
		return this.$querys(".dots > .dot");
	}

	getDot(index: number) {
		return this.$query(`.dots > .dot[data-slide="${ index }"]`);
	}

	$hydrate() {
		super.$hydrate();

		if (this.data.arrows) {
			this.$query(`[data-action="left"]`).addEventListener("click", e => {
				e.preventDefault();

				const wasPlaying = this.$parent.isPlaying();

				this.$parent.stop();
				this.$parent.left();

				if (wasPlaying) {
					this.$parent.start();
				}
			});

			this.$query(`[data-action="right"]`).addEventListener("click", e => {
				e.preventDefault();

				const wasPlaying = this.$parent.isPlaying();

				this.$parent.stop();
				this.$parent.right();

				if (wasPlaying) {
					this.$parent.start();
				}
			});
		}

		if (this.data.dots) {
			const dots = this.$querys(`.dots > .dot`);

			dots.forEach(dot => {
				dot.addEventListener("click", e => {
					const index = parseInt(dot.getAttribute("data-slide"));

					if (this.$parent.currentSlide === index) {
						return;
					}

					const wasPlaying = this.$parent.isPlaying();

					this.$parent.stop();
					this.$parent.goTo(index);

					if (wasPlaying) {
						this.$parent.start();
					}
				});
			});
		}
	}
}

const view = (slideshow: QualitySlideshow) => `
	<div class="viewport">
		<div class="slides">
			${ $each(slideshow.data.slides, (data, i) => QualitySlide.$for(slideshow, i, data)) }
		</div>

		${ QualityControls.$for(slideshow, "$", slideshow.controlsData) }
	</div>
`;

@TagName("quality-slideshow")
@Template(view)
export default class QualitySlideshow extends Component {
	data: SlideshowData;
	slides: QualitySlide[] = [];
	currentSlide: number = 0;

	controlsData: ControlsData;

	protected transitionSpeed: seconds;
	protected interval: any;

	constructor(params: SlideshowData, el?: HTMLElement) {
		super(params, el);

		let missing = [];
		for (let key in slideshowSchema) {
			if (!slideshowSchema.hasOwnProperty(key)) {
				continue;
			}

			if (typeof params[key] !== slideshowSchema[key]) {
				missing.push(key);
			}
		}

		if (missing.length > 0) {
			console.error(`Slideshow data is missing parameters: ${ missing.join(",") }`);
			this.$template = () => "broken, see console";
		}

		this.data = params;
		this.transitionSpeed = this.data.speed || 3;

		if (this.data.controls === true || this.data.controls == null) {
			this.controlsData = {
				arrows: true,
				dots: true
			};
		} else if (this.data.controls === false) {
			this.controlsData = {
				arrows: false,
				dots: false
			};
		} else {
			this.controlsData = this.data.controls;
		}

		this.data.slides.forEach((data, i) => {
			const slide = <QualitySlide>QualitySlide.$for(this, i, data);
			this.slides.push(slide);
		});

		if (this.data.autoPlay) {
			this.start();
		}
	}

	$hydrate() {
		super.$hydrate();

		this.slides[this.currentSlide].goIn();
		this.$element.setAttribute("data-bound", "true");

		this.$onSwitchSlides.forEach(f => f(this.currentSlide));

		const viewport = <HTMLElement>this.$element.querySelector(".viewport");

		if (this.data.aspectRatio) {
			const ratio = new AspectRatio(viewport, this.data.aspectRatio);
		}

		if (this.data.maxHeight) {
			viewport.style.maxHeight = this.data.maxHeight;
		}

		if (this.data.minHeight) {
			viewport.style.minHeight = this.data.minHeight;
		}
	}

	isPlaying() {
		return (this.interval != null);
	}

	stop() {
		if (this.interval != null) {
			clearInterval(this.interval);
			this.interval = null;
		}
	}

	start() {
		this.stop();

		this.interval = setInterval(() => this.right(), this.transitionSpeed * 1000);
	}

	goTo(index: number) {
		const current = this.slides[this.currentSlide];
		const next = this.slides[index];

		this.$onSwitchSlides.forEach(f => f(index));

		this.currentSlide = index;

		next.prepToGoIn()
			.then(() => {
				current.goOut();
				next.goIn();
			});
	}

	protected $onSwitchSlides: Function[] = [];
	onSwitchSlides(callback: Function) {
		this.$onSwitchSlides.push(callback);
	}

	right() {
		let next = this.currentSlide + 1;

		if (next >= this.slides.length) {
			next = 0;
		}

		this.goTo(next);
	}

	left() {
		let next = this.currentSlide - 1;

		if (next < 0) {
			next = this.slides.length - 1;
		}

		this.goTo(next);
	}
}
